import { environment } from 'src/environments/environment';

/**
 * The double-submit CSRF token, and the one place that decides where it comes from.
 *
 * The API mints two cookies on login and keeps them alive on every session bootstrap
 * (`oauth/login-user` → refreshCsrfCookie): the HttpOnly access token, and this readable token. A
 * state-changing request must echo the token back in `X-XSRF-TOKEN`, alongside
 * `X-Auth-Client: backend`, or the API answers `403 Invalid or missing CSRF token.`
 *
 * TWO SOURCES, in priority order — both COOKIES:
 *
 * 1. `environment.csrfCookieName` — this console's own scoped cookie
 *    (`XSRF-TOKEN-admin` + the env suffix). It must track `environment.apiUrl`,
 *    because the suffix comes from that API's `YII_ENV`, and it is the name the
 *    API writes for a caller sending `X-Auth-Client: backend`
 *    (`csrf_cookie_name_backend` in `modules/v3/config/params.php`).
 * 2. `CSRF_COOKIE_LEGACY` — the pre-isolation shared name, for one release only.
 *
 * Reading the cookie requires the page and the API to be same-site: a console on
 * `localhost` against the staging API cannot see a `Domain=.gatherco.com.au`
 * cookie, so local dev must point at the LOCAL stack, where the cookie is
 * host-only on `localhost` and therefore readable across ports.
 *
 * There is deliberately NO response-header source. An earlier version captured a
 * token from an `X-XSRF-TOKEN` RESPONSE header, documented as the fix for exactly
 * that cross-origin case; no API has ever sent that header and it is in no
 * `Access-Control-Expose-Headers` list, so the captured value was always null.
 *
 * Plain functions rather than an @Injectable: the raw XHR in
 * `my-upload-adapter.ts` is constructed with `new` and has no injector.
 */

/** Cookie the API mints the token in (non-HttpOnly, by design). */
export const CSRF_COOKIE: string = environment.csrfCookieName || 'XSRF-TOKEN';

/**
 * The pre-isolation shared name, read only as a FALLBACK.
 *
 * `CSRF_COOKIE` used to BE this value for every app and every environment. At
 * `Domain=.gatherco.com.au` that is ONE cookie shared by this console, the customer site and
 * staging — and each of them rotates the token when it mints a session, so another app could
 * replace it while a write of ours was already on the wire. The server then saw cookie != header
 * and answered `403 Invalid or missing CSRF token.` on a perfectly valid request, which this
 * console turned into a "404 Page not found" screen.
 *
 * TRANSITION — DELETE ME, together with the legacy entry in the API's
 * AuthCookieTrait::csrfAcceptedCookieNames(). It exists so a bundle already running in someone's
 * tab keeps working across the deploy; once every build reads the scoped name it is the last path
 * by which this console could present another app's token.
 */
export const CSRF_COOKIE_LEGACY = 'XSRF-TOKEN';

/** Request header the API expects the token echoed back in. */
export const CSRF_HEADER = 'X-XSRF-TOKEN';


/**
 * The token to present on the next request, or null when neither cookie is
 * readable — logged out, or a console origin that is not same-site with its API.
 */
export function csrfToken(): string | null {
  return readCookie(CSRF_COOKIE) ?? readCookie(CSRF_COOKIE_LEGACY);
}


export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()\[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}
